/**
 * Happy Authentication Service
 *
 * Handles device authentication with Vibe Remote relay server via REST API.
 * Uses URL-based pairing flow for device authentication.
 *
 * Auth Flow:
 * 1. Call initiatePairing() to get a pairing token and URL
 * 2. Daemon displays pairing URL to user
 * 3. User clicks URL, Web UI calls approvePairing() to approve
 * 4. Daemon calls confirmWithCode() to get JWT tokens
 * 5. Tokens are stored in HTTP-only cookies (not localStorage)
 *
 * NOTE: Auth tokens are now stored in HTTP-only cookies for XSS protection.
 * localStorage has been completely removed.
 */

import { useHappyStore } from '../store/happyStore';
import {
  getEncryptionKeypair,
  getPublicKeyBase64,
  generateDeviceId,
} from './happyEncryption';
import {
  initiatePairing as vibeInitiatePairing,
  approvePairing as vibeApprovePairing,
  checkPairingStatus as vibeCheckPairingStatus,
  checkAuthStatus as vibeCheckAuthStatus,
  refreshAccessToken as vibeRefreshToken,
  confirmWithCode as vibeConfirmWithCode,
  getPairingTokenFromUrl as vibeGetPairingTokenFromUrl,
  clearPairingTokenFromUrl as vibeClearPairingTokenFromUrl,
  type InitiatePairingResponse,
  type PairingStatusResponse,
  type AuthStatusResponse as VibeAuthStatusResponse,
} from './vibeRemoteAuth';

// Server URL - DigitalOcean self-hosted deployment
const DEFAULT_SERVER_URL = 'https://tools.eroland.me';

// Cookie names (must match server's COOKIE_NAMES)
const COOKIE_NAMES = {
  ACCESS_TOKEN: 'vibe_access_token',
  REFRESH_TOKEN: 'vibe_refresh_token',
  DEVICE_ID: 'vibe_device_id',
  SESSION_ID: 'vibe_session_id',
} as const;

/**
 * Auth request response from server
 * Server returns { state: 'requested' } or { state: 'authorized', token, response }
 */
interface AuthRequestResponse {
  state: 'requested' | 'authorized';
  token?: string;
  response?: string;
}

/**
 * Auth status response from server
 */
interface AuthStatusResponse {
  state: 'requested' | 'authorized' | 'expired';
  token?: string;
  response?: string;
}

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Get device ID from cookie
 */
export function getDeviceIdFromCookie(): string | null {
  return getCookie(COOKIE_NAMES.DEVICE_ID);
}

/**
 * Get session ID from cookie
 */
export function getSessionIdFromCookie(): string | null {
  return getCookie(COOKIE_NAMES.SESSION_ID);
}

/**
 * Check if we have auth cookies set
 * Note: We can only check non-HTTP-only cookies (device_id, session_id)
 * The access_token and refresh_token are HTTP-only and not accessible via JS
 */
export function hasAuthCookies(): boolean {
  const deviceId = getDeviceIdFromCookie();
  const sessionId = getSessionIdFromCookie();
  return !!(deviceId && sessionId);
}

/**
 * Get stored auth token - DEPRECATED
 * Auth tokens are now in HTTP-only cookies, not accessible via JS.
 * Use hasAuthCookies() to check if authenticated.
 */
export function getStoredToken(): string | null {
  // Access token is in HTTP-only cookie, not accessible via JS
  // Return null - the browser will automatically send the cookie with requests
  console.log('[HappyAuth] getStoredToken is deprecated - tokens are in HTTP-only cookies');
  return null;
}

/**
 * Store auth token - NO-OP
 * Auth tokens are now set by the server via HTTP-only cookies.
 */
export function storeToken(_token: string): void {
  console.log('[HappyAuth] storeToken is NO-OP - tokens are set by server via cookies');
}

/**
 * Clear stored auth data
 * Calls the server's logout endpoint to clear HTTP-only cookies.
 */
export async function clearAuthData(): Promise<void> {
  try {
    const serverUrl = getServerUrl();
    await fetch(`${serverUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include', // Include cookies
    });
    console.log('[HappyAuth] Auth data cleared via server logout');
  } catch (error) {
    console.warn('[HappyAuth] Failed to clear auth data:', error);
  }
}

/**
 * Initialize with CLI token - DEPRECATED
 * Token sharing is handled via cookies set by the server.
 */
export function initializeWithCliToken(): boolean {
  console.log('[HappyAuth] initializeWithCliToken is deprecated - using cookies');
  return false;
}

/**
 * Get server URL from store or default
 */
function getServerUrl(): string {
  return useHappyStore.getState().serverUrl || DEFAULT_SERVER_URL;
}

/**
 * Initiate authentication request
 *
 * Uses the new /auth/pair/init endpoint to start pairing.
 * Returns a compatible AuthRequestResponse for backwards compatibility.
 */
export async function initiateAuthRequest(): Promise<AuthRequestResponse | null> {
  try {
    const serverUrl = getServerUrl();

    console.log('[HappyAuth] Initiating auth request via new pairing API...');

    // Use the new vibeRemoteAuth service
    const pairingResult = await vibeInitiatePairing(serverUrl);

    if (!pairingResult) {
      console.error('[HappyAuth] Pairing initiation failed');
      return null;
    }

    console.log('[HappyAuth] Pairing initiated:', pairingResult.token?.slice(0, 8) + '...');

    // Update store with pairing status (no localStorage)
    useHappyStore.getState().setAuth({
      pairing_code: pairingResult.token.slice(0, 8).toUpperCase(),
      authenticated: false,
    });

    // Return backwards-compatible response
    return {
      state: 'requested',
    };
  } catch (error) {
    console.error('[HappyAuth] Error initiating auth request:', error);
    return null;
  }
}

/**
 * Check auth request status
 *
 * Uses the new /auth/pair/status/:token endpoint to check pairing status.
 * Returns a compatible AuthStatusResponse for backwards compatibility.
 */
export async function checkAuthStatus(pairingToken?: string): Promise<AuthStatusResponse | null> {
  try {
    const serverUrl = getServerUrl();

    if (!pairingToken) {
      console.log('[HappyAuth] No pairing token to check');
      return null;
    }

    // Use the new vibeRemoteAuth service
    const statusResult = await vibeCheckPairingStatus(serverUrl, pairingToken);

    if (!statusResult) {
      console.error('[HappyAuth] Status check failed');
      return { state: 'expired' };
    }

    console.log('[HappyAuth] Pairing status:', statusResult.status);

    // Map the new status to backwards-compatible response
    if (statusResult.status === 'approved') {
      // Pairing approved - update auth state
      useHappyStore.getState().setAuth({
        authenticated: true,
        device_id: statusResult.deviceId || pairingToken.slice(0, 8),
        pairing_code: undefined,
      });

      return {
        state: 'authorized',
      };
    } else if (statusResult.status === 'expired' || statusResult.status === 'used') {
      return { state: 'expired' };
    }

    // Still pending
    return { state: 'requested' };
  } catch (error) {
    console.error('[HappyAuth] Error checking auth status:', error);
    return null;
  }
}

/**
 * Poll for auth approval
 * @param pairingToken The pairing token to poll for
 * @param maxAttempts Maximum polling attempts
 * @param intervalMs Polling interval in milliseconds
 */
export async function pollForApproval(
  pairingToken: string,
  maxAttempts: number = 60,
  intervalMs: number = 2000
): Promise<boolean> {
  console.log('[HappyAuth] Starting polling for approval...');

  for (let i = 0; i < maxAttempts; i++) {
    const status = await checkAuthStatus(pairingToken);

    if (status?.state === 'authorized') {
      console.log('[HappyAuth] Auth approved!');
      return true;
    }

    if (status?.state === 'expired') {
      console.log('[HappyAuth] Auth request expired');
      useHappyStore.getState().setAuth({
        authenticated: false,
        pairing_code: undefined,
      });
      return false;
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  console.log('[HappyAuth] Polling timed out');
  return false;
}

/**
 * Authenticate directly with stored token - DEPRECATED
 *
 * Auth is now cookie-based. Use tryRestoreAuth() instead.
 */
export async function authenticateWithSignature(): Promise<string | null> {
  console.log('[HappyAuth] authenticateWithSignature is deprecated - using cookies');
  return null;
}

/**
 * Try to restore auth from cookies
 *
 * Checks if we have valid auth cookies and validates with server.
 */
export async function tryRestoreAuth(): Promise<boolean> {
  // Check if we have auth cookies
  if (!hasAuthCookies()) {
    console.log('[HappyAuth] No auth cookies found');
    return false;
  }

  const deviceId = getDeviceIdFromCookie();
  console.log('[HappyAuth] Found auth cookies, validating with server...');

  try {
    const serverUrl = getServerUrl();

    // Call /auth/status with credentials to validate cookies
    const response = await fetch(`${serverUrl}/auth/status`, {
      method: 'GET',
      credentials: 'include', // Include HTTP-only cookies
    });

    if (!response.ok) {
      console.log('[HappyAuth] Auth validation failed:', response.status);
      return false;
    }

    const data = await response.json();

    if (data.authenticated) {
      console.log('[HappyAuth] Auth cookies are valid');
      useHappyStore.getState().setAuth({
        authenticated: true,
        device_id: data.deviceId || deviceId || 'unknown',
      });
      return true;
    } else {
      console.log('[HappyAuth] Auth cookies are invalid, clearing...');
      await clearAuthData();
      return false;
    }
  } catch (error) {
    // On network error, trust the cookies if they exist
    console.warn('[HappyAuth] Auth validation failed (network), trusting cookies');
    useHappyStore.getState().setAuth({
      authenticated: true,
      device_id: deviceId || 'unknown',
    });
    return true;
  }
}

/**
 * Full authentication flow
 * 1. Try to restore from cookies
 * 2. If no valid cookies, return false (pairing needed)
 */
export async function authenticate(): Promise<boolean> {
  // Try to restore existing auth
  if (await tryRestoreAuth()) {
    return true;
  }

  // No valid auth - pairing needed
  return false;
}

// ============================================
// URL-BASED PAIRING
// ============================================

/**
 * Approve URL-based pairing with a token from the URL query parameter
 * This is called when the user clicks a pairing URL from the daemon.
 *
 * IMPORTANT: This calls /auth/pair/approve which marks the pairing as approved
 * WITHOUT consuming the token. The CLI daemon will then call /auth/pair/token/confirm
 * to get the actual JWT tokens (which will be set as HTTP-only cookies).
 *
 * @param pairingToken - The token from the URL query parameter (?pair=<token>)
 * @returns True if approval succeeded, false otherwise
 */
export async function confirmUrlPairing(pairingToken: string): Promise<boolean> {
  try {
    const serverUrl = getServerUrl();

    console.log('[HappyAuth] Approving URL-based pairing...');

    const response = await fetch(`${serverUrl}/auth/pair/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies
      body: JSON.stringify({
        token: pairingToken,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[HappyAuth] URL pairing approval failed:', response.status, errorData);

      useHappyStore.getState().setAuth({
        authenticated: false,
        pairing_code: undefined,
      });

      return false;
    }

    const data = await response.json();
    console.log('[HappyAuth] URL pairing approval response:', data);

    if (data.success) {
      useHappyStore.getState().setAuth({
        authenticated: true,
        device_id: data.deviceId || 'PAIRED-DEVICE',
        pairing_code: undefined,
      });

      console.log('[HappyAuth] URL pairing approved! CLI is now authenticated.');
      return true;
    }

    console.error('[HappyAuth] URL pairing approval: Unexpected response', data);
    return false;
  } catch (error) {
    console.error('[HappyAuth] Error approving URL pairing:', error);
    return false;
  }
}

/**
 * Extract pairing token from URL query parameters
 *
 * @returns The pairing token if present, null otherwise
 */
export function getPairingTokenFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('pair');
}

/**
 * Remove pairing token from URL without page reload
 * Called after successful pairing to clean up the URL
 */
export function clearPairingTokenFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('pair');
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Fetch sessions from server via Socket.IO
 *
 * NOTE: Sessions are now managed via Socket.IO, not REST API.
 * This function returns an empty array - sessions are received via socket events.
 *
 * @deprecated Use Socket.IO session events instead
 */
export async function fetchSessions(): Promise<any[]> {
  console.log('[HappyAuth] Sessions are now managed via Socket.IO, not REST API');
  return [];
}

// ============================================
// Re-export utility functions from vibeRemoteAuth
// ============================================

export { vibeGetPairingTokenFromUrl as getPairingTokenFromUrlNew };
export { vibeClearPairingTokenFromUrl as clearPairingTokenFromUrlNew };
export { vibeApprovePairing as approvePairingNew };
export { vibeConfirmWithCode as confirmWithCodeNew };
export { vibeRefreshToken as refreshAccessTokenNew };

// Re-export daemon status check for skip-pairing flow
export { checkDaemonStatus } from './vibeRemoteAuth';
export type { DaemonStatusResponse } from './vibeRemoteAuth';
